import { useCallback, useEffect, useRef, useState } from "react";
import { formatCopyBlock, formatCopyItem, type CopyItem } from "./copy-format.ts";
import { evidenceLabel } from "./evidence.ts";
import { formatCaseCitation } from "./local-provider.ts";
import type { SearchResult } from "./types";

/**
 * 인용 바구니.
 *
 * 저장은 호스트가 주입한 `BasketStorage`로만 한다.
 * - 확장: `chrome.storage.local`
 * - 로컬 데모: `localStorage`
 * - 주입이 없으면 메모리(새로고침 시 사라짐)
 *
 * 저장 단위는 문자열이 아니라 `CopyItem`이다. 그래야 클립보드 출력이
 * `formatCopyBlock` 하나로 결정되고, 머리말/안내문이 끼어들지 않는다.
 */
export const BASKET_STORAGE_KEY = "beoptong.basket.v1";

export interface BasketStorage {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
}

export interface BasketItem extends CopyItem {
  /** SearchResult.pointer */
  id: string;
}

export const memoryBasketStorage: BasketStorage = {
  async get() {
    return null;
  },
  async set() {
    // no-op
  },
};

/** localStorage 기반 저장소. 데모/웹에서 쓴다. */
export function createLocalBasketStorage(storage?: Storage): BasketStorage {
  const target =
    storage ?? (typeof window !== "undefined" ? window.localStorage : undefined);
  return {
    async get(key) {
      try {
        return target?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    async set(key, value) {
      try {
        target?.setItem(key, value);
      } catch {
        // 사생활 보호 모드 등에서 quota 오류가 나도 검색은 계속되어야 한다.
      }
    },
  };
}

interface LegacyBasketItem {
  id?: unknown;
  kind?: unknown;
  label?: unknown;
  citation?: unknown;
  title?: unknown;
  quote?: unknown;
  quoteLabel?: unknown;
}

function nonEmpty(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * v1 저장값을 방어적으로 읽는다.
 *
 * - 문자열 항목(가장 오래된 형식): 조문 인용 한 줄로 본다.
 * - `{id, label, citation, quote}`(구 형식): label은 조문 라벨, citation은
 *   판례 인용이었다. 판례 제목 `[…]`를 새로 만들지 않고 citation만 남긴다.
 * - `{id, kind, citation, title?, quote?, quoteLabel?}`(현재 형식).
 */
function normalizeBasketItem(value: unknown): BasketItem | null {
  if (typeof value === "string") {
    const citation = nonEmpty(value);
    return citation ? { id: citation, kind: "statute", citation } : null;
  }
  if (typeof value !== "object" || value === null) return null;

  const raw = value as LegacyBasketItem;
  const legacyCitation = nonEmpty(raw.citation);
  const legacyLabel = nonEmpty(raw.label);
  const id = nonEmpty(raw.id) ?? legacyCitation ?? legacyLabel;
  const citation = legacyCitation ?? legacyLabel ?? id;
  if (!citation) return null;

  const kind =
    raw.kind === "case" || raw.kind === "statute"
      ? raw.kind
      : legacyCitation
        ? "case"
        : "statute";
  const title = nonEmpty(raw.title);
  const quote = nonEmpty(raw.quote);
  const quoteLabel = nonEmpty(raw.quoteLabel);

  return {
    id: id ?? citation,
    kind,
    citation,
    ...(title ? { title } : {}),
    ...(quote ? { quote } : {}),
    ...(quoteLabel ? { quoteLabel } : {}),
  };
}

export function parseBasket(raw: string | null): BasketItem[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const items: BasketItem[] = [];
    const seen = new Set<string>();
    for (const entry of parsed) {
      const item = normalizeBasketItem(entry);
      if (!item || seen.has(item.id)) continue;
      seen.add(item.id);
      items.push(item);
    }
    return items;
  } catch {
    return [];
  }
}

/** 복사 첫 줄. 판례는 판결 인용, 조문은 조문 라벨. */
export function basketLabel(result: SearchResult): string {
  if (result.caseNo) {
    return (
      result.citation ??
      formatCaseCitation({
        pointer: result.pointer,
        court: result.court,
        caseNo: result.caseNo,
        decisionDate: result.decisionDate,
      })
    );
  }
  return result.citation ?? result.label ?? result.pointer;
}

/**
 * SearchResult → 저장용 CopyItem.
 *
 * quote를 명시하면 그 문장을 쓴다(리더 선택 영역). 명시하지 않으면 provider가
 * 붙인 첫 근거 문장(`result.quote`)을 쓰고, 라벨도 그 문장의 라벨을 쓴다.
 * 판례 제목 `[…]`는 데이터에 있을 때만 넣는다(현재 SearchResult에는 없다).
 */
export function toBasketItem(
  result: SearchResult,
  quote?: string,
  quoteLabel?: string,
): BasketItem {
  const isCase = Boolean(result.caseNo);
  const citation = basketLabel(result);
  const explicit = nonEmpty(quote);
  const fallback = nonEmpty(result.quote);
  const first = result.evidenceSentences?.[0];
  const finalQuote = explicit ?? fallback;
  const finalLabel = explicit
    ? nonEmpty(quoteLabel)
    : fallback && first
      ? evidenceLabel(first)
      : nonEmpty(quoteLabel);

  return {
    id: result.pointer,
    kind: isCase ? "case" : "statute",
    citation,
    ...(finalQuote ? { quote: finalQuote } : {}),
    ...(finalLabel ? { quoteLabel: finalLabel } : {}),
  };
}

/** 바구니 한 건을 클립보드 형식으로. (문자열 → CopyItem 경계) */
export function formatBasketItem(item: BasketItem): string {
  return formatCopyItem(item);
}

/** 바구니 전체를 클립보드 형식으로. `formatCopyBlock`이 최종 출력이다. */
export function basketText(items: readonly BasketItem[]): string {
  return formatCopyBlock([...items]);
}

/** clipboard API → textarea fallback. 성공 여부를 돌려준다. */
export async function copyText(text: string): Promise<boolean> {
  if (!text) return false;
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // 권한이 없으면 fallback으로 넘어간다.
  }

  if (typeof document === "undefined") return false;
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.top = "-1000px";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    area.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    area.remove();
    return ok;
  } catch {
    return false;
  }
}

export type BasketCopyState = "idle" | "done" | "fail";

export interface BasketController {
  items: BasketItem[];
  ready: boolean;
  copyState: BasketCopyState;
  has(id: string): boolean;
  add(result: SearchResult, quote?: string): void;
  toggle(result: SearchResult): void;
  remove(id: string): void;
  clear(): void;
  copy(): Promise<boolean>;
}

export function useBasket(storage: BasketStorage = memoryBasketStorage): BasketController {
  const [items, setItems] = useState<BasketItem[]>([]);
  const [ready, setReady] = useState(false);
  const [copyState, setCopyState] = useState<BasketCopyState>("idle");
  const itemsRef = useRef<BasketItem[]>(items);
  itemsRef.current = items;
  const storageRef = useRef(storage);
  storageRef.current = storage;

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    void storageRef.current
      .get(BASKET_STORAGE_KEY)
      .then((raw) => {
        if (cancelled) return;
        const loaded = parseBasket(raw);
        setItems((current) => {
          if (current.length === 0) return loaded;
          const ids = new Set(current.map((entry) => entry.id));
          return [...loaded.filter((entry) => !ids.has(entry.id)), ...current];
        });
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [storage]);

  const persist = useCallback((next: BasketItem[]) => {
    setItems(next);
    void storageRef.current
      .set(BASKET_STORAGE_KEY, JSON.stringify(next))
      .catch(() => undefined);
  }, []);

  const add = useCallback(
    (result: SearchResult, quote?: string) => {
      const item = toBasketItem(result, quote);
      const current = itemsRef.current;
      const existing = current.find((entry) => entry.id === item.id);
      const next = existing
        ? current.map((entry) =>
            entry.id === item.id
              ? {
                  ...entry,
                  ...item,
                  quote: item.quote ?? entry.quote,
                  // 새 선택 인용문에는 라벨이 없다. 예전 문장 라벨을 붙이지 않는다.
                  quoteLabel: item.quoteLabel ?? (item.quote ? undefined : entry.quoteLabel),
                }
              : entry,
          )
        : [...current, item];
      persist(next);
    },
    [persist],
  );

  const toggle = useCallback(
    (result: SearchResult) => {
      const current = itemsRef.current;
      if (current.some((entry) => entry.id === result.pointer)) {
        persist(current.filter((entry) => entry.id !== result.pointer));
      } else {
        persist([...current, toBasketItem(result)]);
      }
    },
    [persist],
  );

  const remove = useCallback(
    (id: string) => {
      persist(itemsRef.current.filter((entry) => entry.id !== id));
    },
    [persist],
  );

  const clear = useCallback(() => {
    persist([]);
  }, [persist]);

  const has = useCallback(
    (id: string) => itemsRef.current.some((entry) => entry.id === id),
    [],
  );

  const copy = useCallback(async () => {
    const text = basketText(itemsRef.current);
    const ok = await copyText(text);
    setCopyState(ok ? "done" : "fail");
    setTimeout(() => setCopyState("idle"), ok ? 1400 : 1800);
    return ok;
  }, []);

  return { items, ready, copyState, has, add, toggle, remove, clear, copy };
}
